import {StyleSheet} from 'react-native';
import Typography from '../../../../shared/constants/Typography';
import Colors from '../../../../shared/theme/colors';
import {CLOCK_SIZE} from './DatePicker.constants';
import {palette} from '@shared/theme/colorGroups/palette.colors';

// ─────────────────────────────────────────────────────────────────────────────
// Calendar theme — passed directly to react-native-calendars <Calendar theme>
// Uses stylesheet overrides to control day-names row and date-number row layout
// ─────────────────────────────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createCalendarTheme = (theme: any): any => ({
  backgroundColor: theme.workOrderStatausCalenderBG,
  calendarBackground: theme.workOrderStatausCalenderBG,
  textSectionTitleColor: theme.calenderTextColor,
  // selectedDayBackgroundColor/TextColor are overridden by stylesheet.day.basic below;
  // kept here as fallback for older versions of the library
  selectedDayBackgroundColor: palette.blue.paleSecondary,
  selectedDayTextColor: palette.ui.hoverBlue,
  todayTextColor: Colors.primaryLight,
  dayTextColor: theme.calenderTextColor,
  textDisabledColor: theme.calenderDisabledText,
  arrowColor: theme.calenderTextColor,
  monthTextColor: theme.calenderTextColor,
  textDayFontSize: Typography.fontSize.fontSize16,
  textDayHeaderFontSize: Typography.fontSize.fontSize13,
  textDayFontWeight: Typography.fontWeight.medium,
  // ── Day-names row (Su Mo Tu We Th Fr Sa) ──────────────────────────────────
  'stylesheet.calendar.header': {
    week: {
      marginTop: Typography.margin.margin8,
      marginBottom: Typography.margin.margin4,
      flexDirection: 'row',
      justifyContent: Typography.contentAlign.spaceAround,
      paddingHorizontal: Typography.padding.padding16,
    },
  },
  // ── Date number rows ───────────────────────────────────────────────────────
  'stylesheet.calendar.main': {
    week: {
      marginTop: Typography.margin.margin4,
      marginBottom: Typography.margin.margin4,
      flexDirection: 'row',
      justifyContent: Typography.contentAlign.spaceAround,
      paddingHorizontal: Typography.padding.padding16,
    },
  },
  // ── Individual day cell ────────────────────────────────────────────────────
  'stylesheet.day.basic': {
    base: {
      width: Typography.width.width40,
      height: Typography.width.width40,
      alignItems: Typography.textAlign.centerAlign,
      justifyContent: Typography.textAlign.centerAlign,
    },
    text: {
      marginTop: 0,
      fontSize: Typography.fontSize.fontSize16,
      backgroundColor: 'transparent',
      textAlign: Typography.textAlign.centerAlign,
      color: theme.calenderTextColor,
    },
    // Selected day: light-blue fill + blue border + blue text (matching image)
    selected: {
      backgroundColor: palette.blue.paleSecondary,
      borderRadius: Typography.radius.radius4,
      borderWidth: Typography.borderWidth.borderWidth2,
      borderColor: palette.ui.hoverBlue,
    },
    selectedText: {
      color: palette.ui.hoverBlue,
      fontWeight: Typography.fontWeight.semiBold,
    },
    today: {
      borderRadius: Typography.radius.radius4,
    },
  },
});

export const createDatePickerStyles = (
  theme: any,
  calendarPos: any,
  _screenHeight: number = 800,
) =>
  StyleSheet.create({
    backdrop: {
      flex: 1,
      backgroundColor: 'transparent',
      justifyContent: Typography.textAlign.centerAlign,
      padding: Typography.padding.padding16,
    },
    card: {
      borderRadius: Typography.radius.radius12,
      padding: Typography.radius.radius12,
    },
    title: {
      fontSize: Typography.fontSize.fontSize16,
      fontWeight: Typography.fontWeight.semiBold,
      marginBottom: Typography.margin.margin8,
    },
    // Container positioned near the input field
    calendarContainer: {
      position: Typography.position.absolute,
      backgroundColor: theme.workOrderStatausCalenderBG,
      borderRadius: Typography.radius.radius12,
      top: calendarPos.top,
      left: calendarPos.left,
      borderColor: theme.workOrderStatausBorder,
      borderWidth: Typography.borderWidth.borderWidth1,
      overflow: 'hidden',
      // maxHeight is computed in openCalendar to fit the available space on
      // whichever side (above / below) the popup is shown.  overflow:auto is
      // applied inline on web so a scrollbar appears when content exceeds this.
      maxHeight: calendarPos.maxHeight ?? 600,
    },
    // Inner scroll container — allows the calendar or time card to scroll
    // when the available viewport height is smaller than the card content.
    calendarScroll: {
      flexGrow: 0,
    },

    // ── Side-by-side row ─────────────────────────────────────────────────────
    sideByRow: {
      flexDirection: Typography.flexDirection.row,
      alignItems: 'stretch',
    },

    // ── LEFT: Calendar card ──────────────────────────────────────────────────
    calendarCard: {
      width: Typography.width.width380,
      overflow: 'hidden',
      paddingBottom: Typography.padding.padding8,
    },
    calendarHeader: {
      backgroundColor: palette.blue.primary,
      paddingHorizontal: Typography.padding.padding16,
      paddingTop: Typography.padding.padding16,
      paddingBottom: Typography.padding.padding10,
    },
    calendarHeaderYear: {
      color: Colors.white,
      fontSize: Typography.fontSize.fontSize14,
      fontFamily: Typography.fontFamily.inter,
      opacity: 0.8,
    },
    calendarHeaderDate: {
      color: Colors.white,
      fontSize: Typography.fontSize.fontSize24,
      fontWeight: Typography.fontWeight.semiBold,
      fontFamily: Typography.fontFamily.inter,
    },

    // ── RIGHT: Time card ─────────────────────────────────────────────────────
    timeCard: {
      width: Typography.width.width300,
      backgroundColor: theme.workOrderStatausCalenderBG,
      borderLeftWidth: Typography.borderWidth.borderWidth1,
      borderLeftColor: theme.workOrderStatausBorder,
    },
    timeHeader: {
      backgroundColor: palette.blue.primary,
      paddingHorizontal: Typography.padding.padding16,
      paddingTop: Typography.padding.padding16,
      paddingBottom: Typography.padding.padding10,
    },
    clockModeLabel: {
      color: Colors.white,
      fontSize: Typography.fontSize.fontSize12,
      fontFamily: Typography.fontFamily.inter,
      opacity: 0.75,
      marginTop: Typography.margin.margin4,
    },
    timeDisplayRow: {
      flexDirection: Typography.flexDirection.row,
      alignItems: Typography.textAlign.centerAlign,
    },
    timeDigit: {
      color: Colors.white,
      fontSize: Typography.fontSize.fontSize36,
      fontWeight: Typography.fontWeight.semiBold,
      fontFamily: Typography.fontFamily.inter,
      opacity: 0.6,
    },
    timeDigitActive: {
      opacity: 1,
    },
    timeInput: {
      width: Typography.width.width50,
      minWidth: Typography.width.width50,
      textAlign: Typography.textAlign.centerAlign,
      backgroundColor: 'transparent',
      padding: 0,
    },
    timeColon: {
      color: Colors.white,
      fontSize: Typography.fontSize.fontSize36,
      fontWeight: Typography.fontWeight.semiBold,
      marginHorizontal: Typography.margin.margin4,
    },
    ampmColumn: {
      marginLeft: Typography.margin.margin8,
      justifyContent: Typography.textAlign.centerAlign,
    },
    ampmText: {
      color: Colors.white,
      fontSize: Typography.fontSize.fontSize14,
      fontFamily: Typography.fontFamily.inter,
      opacity: 0.6,
      paddingVertical: Typography.padding.padding2,
    },
    ampmActive: {
      opacity: 1,
      fontWeight: Typography.fontWeight.semiBold,
    },
    clockWrapper: {
      alignItems: Typography.textAlign.centerAlign,
      paddingVertical: Typography.padding.padding16,
    },
    // ── AnalogClock container (fixed square matching CLOCK_SIZE) ──────────────
    clockContainer: {
      width: CLOCK_SIZE,
      height: CLOCK_SIZE,
    },
    // ── Inner Pressable (stopPropagation wrapper) — shrinks to content on web ─
    contentPressable: {
      alignSelf: Typography.contentAlign.flexStart,
    },
    timeActions: {
      flexDirection: Typography.flexDirection.row,
      justifyContent: Typography.contentAlign.flexEnd,
      paddingHorizontal: Typography.padding.padding16,
      paddingBottom: Typography.padding.padding16,
      gap: Typography.gap.gap8,
    },
    timeActionBtn: {
      paddingHorizontal: Typography.padding.padding8,
      paddingVertical: Typography.padding.padding8,
    },
    timeActionText: {
      color: Colors.primaryLight,
      fontSize: Typography.fontSize.fontSize14,
      fontWeight: Typography.fontWeight.semiBold,
      fontFamily: Typography.fontFamily.inter,
    },
    timeActionDisabled: {
      opacity: 0.35,
    },

    // ── Legacy / unused (kept to avoid breakage) ─────────────────────────────
    calendarWidget: {width: Typography.width.width320},
    verticalDivider: {
      width: Typography.borderWidth.borderWidth1,
      alignSelf: 'stretch',
      backgroundColor: theme.workOrderStatausBorder,
      marginHorizontal: Typography.margin.margin8,
    },
    timeDrumPanel: {
      flex: 1,
      alignItems: Typography.textAlign.centerAlign,
      justifyContent: Typography.textAlign.centerAlign,
      padding: Typography.padding.padding16,
      gap: Typography.gap.gap16,
    },
    drumRow: {
      flexDirection: Typography.flexDirection.row,
      alignItems: Typography.textAlign.centerAlign,
      justifyContent: Typography.textAlign.centerAlign,
      gap: Typography.gap.gap4,
    },
    confirmButton: {
      backgroundColor: Colors.primaryLight,
      borderRadius: Typography.radius.radius8,
      paddingVertical: Typography.padding.padding10,
      paddingHorizontal: Typography.padding.padding16,
      alignItems: Typography.textAlign.centerAlign,
      width: Typography.fullWidth,
    },
    confirmButtonDisabled: {opacity: 0.45},
    confirmButtonText: {
      color: Colors.white,
      fontSize: Typography.fontSize.fontSize14,
      fontWeight: Typography.fontWeight.semiBold,
      fontFamily: Typography.fontFamily.inter,
    },
    timePicker: {
      paddingVertical: Typography.padding.padding16,
      paddingHorizontal: Typography.padding.padding8,
      alignItems: Typography.textAlign.centerAlign,
    },
    timeHeader_legacy: {
      flexDirection: Typography.flexDirection.row,
      alignItems: Typography.textAlign.centerAlign,
      width: Typography.fullWidth,
      marginBottom: Typography.margin.margin16,
    },
    backButton: {paddingRight: Typography.padding.padding8},
    backButtonText: {
      fontSize: Typography.fontSize.fontSize24,
      color: theme.calenderTextColor,
    },
    timeHeaderLabel: {
      flex: 1,
      fontSize: Typography.fontSize.fontSize14,
      fontWeight: Typography.fontWeight.medium,
      color: theme.calenderTextColor,
      fontFamily: Typography.fontFamily.inter,
    },
    timeHeaderTitle: {
      fontSize: Typography.fontSize.fontSize14,
      fontWeight: Typography.fontWeight.semiBold,
      color: theme.calenderTextColor,
      fontFamily: Typography.fontFamily.inter,
    },
    timeRow: {
      flexDirection: Typography.flexDirection.row,
      alignItems: Typography.textAlign.centerAlign,
      justifyContent: Typography.textAlign.centerAlign,
      gap: Typography.gap.gap16,
      marginBottom: Typography.margin.margin16,
    },
    timeColumn: {
      alignItems: Typography.textAlign.centerAlign,
      gap: Typography.gap.gap8,
    },
    timeArrow: {
      paddingHorizontal: Typography.padding.padding16,
      paddingVertical: Typography.padding.padding8,
    },
    timeArrowText: {
      fontSize: Typography.fontSize.fontSize18,
      color: theme.calenderTextColor,
    },
    timeValue: {
      fontSize: Typography.fontSize.fontSize32,
      fontWeight: Typography.fontWeight.semiBold,
      color: theme.calenderTextColor,
      fontFamily: Typography.fontFamily.inter,
      minWidth: Typography.width.width56,
      textAlign: Typography.textAlign.centerAlign,
    },
    timeUnitLabel: {
      fontSize: Typography.fontSize.fontSize12,
      color: theme.calenderDisabledText,
      fontFamily: Typography.fontFamily.inter,
    },
    timeSeparator: {
      fontSize: Typography.fontSize.fontSize32,
      fontWeight: Typography.fontWeight.bold,
      color: theme.calenderTextColor,
      marginBottom: Typography.margin.margin16,
    },
    divider: {
      height: Typography.height.height1,
      backgroundColor: theme.workOrderStatausBorder,
      marginVertical: Typography.margin.margin8,
    },
    scrollViews: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: Typography.contentAlign.spaceBetween,
      gap: Typography.gap.gap8,
    },
  });
